import { useState } from "react";
import { useGetIdentity, useNotify } from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { ListPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { enroll, useSequences, type Sequence } from "./enroll";
import { HelpTooltip } from "../help/HelpTooltip";

/** "Inscribir en cadencia" para un contacto (y opcionalmente su oportunidad). */
export const EnrollButton = ({
  contactId,
  companyId,
  dealId,
  offerId,
  offerIds,
}: {
  contactId?: number | null;
  companyId?: number | null;
  dealId?: number | null;
  offerId?: number | null;
  /** desde la ficha: solo cadencias de los productos donde tiene oportunidad */
  offerIds?: number[];
}) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { data: sequences } = useSequences();
  const { identity } = useGetIdentity();
  const notify = useNotify();
  const queryClient = useQueryClient();

  if (!contactId) return null;
  // desde una oportunidad, solo las cadencias de su oferta
  const choices = (sequences ?? []).filter(
    (s) =>
      (!offerId || s.offer_id === offerId) &&
      (!offerIds || offerIds.includes(s.offer_id ?? -1)),
  );

  const pick = async (s: Sequence) => {
    setBusy(true);
    try {
      await enroll({
        sequenceId: s.id,
        offerId: s.offer_id,
        contactId,
        companyId,
        dealId,
        salesId: Number(identity?.id),
      });
      notify(`Inscrito en ${s.name}: sus tareas ya están en Mi día`, {
        type: "success",
      });
      ["my_day", "tasks", "ficha_tasks", "deal_board"].forEach((k) =>
        queryClient.invalidateQueries({ queryKey: [k] }),
      );
      setOpen(false);
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    }
    setBusy(false);
  };

  return (
    <>
      <div className="inline-flex items-center gap-1">
        <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
          <ListPlus />
          Inscribir en cadencia
        </Button>
        <HelpTooltip topic="sequences.enroll" />
      </div>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Inscribir en cadencia</DialogTitle>
            <DialogDescription>
              Se crean sus tareas (llamar, WhatsApp, correo) en los días de cada
              paso.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            {choices.length ? (
              choices.map((s) => (
                <Button
                  key={s.id}
                  variant="outline"
                  className="h-12 text-base"
                  disabled={busy}
                  onClick={() => pick(s)}
                >
                  {s.name}
                </Button>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">
                No hay cadencias activas para sus productos. Pásala primero a un
                producto o crea la cadencia en IA y ajustes → Cadencias.
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
